import { createContext, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { isEmpty } from 'ramda'
import { Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik'
import { Userpilot } from 'userpilot'
import type { StringSchema } from 'yup'

import ENV_VARS from '@/config'
import prices from '@/lib/prices'
import { useDispatch } from '@/lib/redux'
import Yup from '@/lib/yup'
import useCurrentSession from '@/hooks/use-current-session'
import usePaymentMethods, { PaymentMethod } from '@/hooks/use-payment-methods'
import useTaxCode from '@/hooks/use-tax-code'
import {
  AccountType,
  BillingType,
  getPaymentPlan
} from '@/features/Pricing/pricing-plans'

import { PaymentInfo } from './types'

const { DASHBOARD_URL } = ENV_VARS

type Props = {
  children?: React.ReactNode
  accountType: AccountType
  subscriptionId?: string
  changePlanRequest: boolean
  listingId?: string
}

type CardContextProps = Omit<Props, 'children'> & {
  cardInfoValid: boolean
  cardMethodValid: boolean
  edit: boolean
  setCardInfoValid: (s: boolean) => void
  setCardMethodValid: (s: boolean) => void
  setEdit: (s: boolean) => void
  card: PaymentMethod
  loading: boolean
}

const Context = createContext<CardContextProps | undefined>(undefined)

const CardProvider: React.FC<Props> = ({
  children,
  subscriptionId,
  accountType,
  changePlanRequest
}) => {
  const [cardInfoValid, setCardInfoValid] = useState(false)
  const [cardMethodValid, setCardMethodValid] = useState(false)
  const [edit, setEdit] = useState(false)
  const { data: card, loading } = usePaymentMethods()
  return (
    <Context.Provider
      value={{
        cardInfoValid,
        cardMethodValid,
        edit,
        setCardInfoValid,
        setCardMethodValid,
        setEdit,
        card,
        loading,
        subscriptionId,
        accountType,
        changePlanRequest
      }}
    >
      {children}
    </Context.Provider>
  )
}

const useCard = (): CardContextProps => {
  const context = useContext(Context)
  if (typeof context === 'undefined') {
    throw new Error('usePaymentForm must be used with a PaymentFormProvider')
  }
  return context
}

export type FormValues = PaymentInfo & {
  frequency: BillingType
  account_type: AccountType
  agree_to_terms: boolean
}

export const usePaymentForm = (): FormikProps<FormValues> &
  CardContextProps & { state?: string } => {
  const { cardInfoValid, cardMethodValid, card, edit, ...rest } = useCard()
  const { values, isValid, ...data } = useFormikContext<FormValues>()
  return {
    ...data,
    ...rest,
    values,
    state: !edit ? card?.state : values?.state,
    isValid: isValid && cardInfoValid && cardMethodValid,
    cardInfoValid,
    cardMethodValid,
    card,
    edit
  }
}

const PaymentForm: React.FC<{
  listingId?: string
  children?: React.ReactNode
}> = ({ children, listingId }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const dispatch = useDispatch()
  const { getValue: getTaxCode } = useTaxCode()
  const {
    setEdit,
    card,
    accountType,
    subscriptionId,
    changePlanRequest,
    loading
  } = useCard()
  const profile = useCurrentSession()
  const hasSavedCard = !isEmpty(card)
  const { query } = router
  const billingCycle = query?.['billing-cycle'] as BillingType
  const promoCode = (query.couponCode ?? '') as String

  useEffect(() => {
    if (loading || hasSavedCard) return
    setEdit(true)
  }, [setEdit, loading, hasSavedCard])
  const initialValues: FormValues = {
    first_name: profile?.first_name ?? '',
    last_name: profile?.last_name ?? '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    country: 'ca',
    zip: '',
    card_number: '',
    expiration_date: '',
    security_code: '',
    save_billing_address: true,
    account_type: accountType,
    frequency: billingCycle ?? 'annual',
    agree_to_terms: false,
    tax_code: ''
  }
  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(),
    last_name: Yup.string().required(),
    expiration_date: Yup.string().length(4).required(),
    security_code: Yup.string().min(3).max(4).required(),
    card_number: Yup.string().required(),
    street1: Yup.string().required(),
    street2: Yup.string(),
    city: Yup.string().required(),
    country: Yup.string().required(),
    state: Yup.string().required(),
    zip: Yup.string().required(),
    save_billing_address: Yup.boolean().required(),
    account_type: Yup.string().required(),
    frequency: Yup.string().required(),
    agree_to_terms: Yup.boolean()
      .oneOf(
        [true],
        t(
          'b.auth.create.must_accept_conditions.text',
          'Must Accept Terms and Conditions'
        )
      )
      .required(),
    tax_code: Yup.string().when(['country', 'state'], ((
      country: string,
      state: string,
      schema: StringSchema
    ) => {
      const taxCode = getTaxCode(country, state)
      if ('format' in taxCode && taxCode.format) {
        return schema.matches(taxCode.format, taxCode.error)
      }
      return schema
    }) as any)
  })

  const onSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    Userpilot.track(`landlord_existing_${values.account_type}_paynow_click`, {
      referrer: document?.referrer
    })
    const { account_type, frequency, ...rest } = values
    const paymentPlan = getPaymentPlan(account_type, frequency)
    const { error } = await dispatch.plans.subscribe({
      ...rest,
      intended_province: values.state,
      plan_name: paymentPlan,
      original_subscription_id: subscriptionId,
      payment_method: 'stripe',
      change_plan_request: changePlanRequest,
      couponCode: promoCode
    })
    setSubmitting(false)
    if (error) return
    Userpilot.track(`landlord_existing_${values.account_type}_paynow_submit`, {
      referrer: document?.referrer
    })

    const planPrice = prices.plans[account_type]

    const priceForTracking =
      frequency === 'monthly'
        ? planPrice.monthly
        : (parseFloat(planPrice.annual) * 12).toString()

    Userpilot.track(
      query?.newAccount
        ? `goal_payment_landlord_signup_${account_type}`
        : `goal_landlord_upgrade_${account_type}`,
      {
        price: priceForTracking
      }
    )

    if (listingId) {
      window.dataLayer.push({
        couponCode: 'alberta',
        event: 'abgrowthfree_postalisting_click_paynow'
      })
      window.location.href = `${DASHBOARD_URL}/listing/create/${listingId}?couponCode=alberta`
    } else {
      router.push({
        pathname: '/register/confirmation',
        query: { accountType }
      })
    }
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {() => children}
    </Formik>
  )
}

const PaymentFormProvider: React.FC<Props> = ({
  children,
  subscriptionId,
  accountType,
  changePlanRequest,
  listingId,
  ...rest
}) => (
  <CardProvider
    subscriptionId={subscriptionId}
    accountType={accountType}
    changePlanRequest={changePlanRequest}
  >
    <PaymentForm listingId={listingId} {...rest}>
      {children}
    </PaymentForm>
  </CardProvider>
)

export default PaymentFormProvider
